import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, ReferenceLine, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getRateColor, getRateDiffColor, getValueColor } from '../../utils/coloring';
import { useChartState } from '../../hooks/charts';
import { resolveRatesChartMenuSelection } from '../../utils/chartMapSync';
import './Charts.css';

const HistoricalRatesChart = ({ 
  selectedLocation, 
  selectedCrime, 
  onChartOptionChange, 
  selectedChartOption, 
  selectedSource,
  compareMode
}) => {
  const [selectedChartState, setSelectedChart] = useState(selectedChartOption);
  const {
    isMobile,
    windowWidth,
    isMenuOpen,
    setIsMenuOpen,
    isTouching,
    setIsTouching,
    menuRef,
    getDropdownLeftPosition
  } = useChartState();

  const { info, data } = selectedLocation;
  const locationType = info?.type?.charAt(0).toUpperCase() + info?.type?.slice(1);
  const isNation = info?.type?.toLowerCase() === 'nation';
  const isCounty = info?.type?.toLowerCase() === 'county';
  const isCity = info?.type?.toLowerCase() === 'city';
  const isMetro = info?.type?.toLowerCase() === 'metro';
  const isCountyOrCity = isCounty || isCity;
  const crimeName = getCrimeDisplayName(selectedCrime, selectedSource);
  
  const stateAbbr = React.useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  const getComparisonLabel = useCallback(() => {
    if (isMetro) {
      return compareMode.rateType === 'metro' ? 'Metro' : 'National';
    }
    if (isCountyOrCity) {
      if (compareMode.rateType === 'county' || compareMode.rateType === 'city') {
        const peerLocLabel = compareMode.rateType === 'county' ? 'County' : 'City';
        return compareMode.rateScope === 'state'
          ? `${stateAbbr} ${peerLocLabel}`
          : `US ${peerLocLabel}`;
      }
      return compareMode.rateScope === 'state'
        ? `${stateAbbr} State`
        : 'US National';
    }
    return '';
  }, [isMetro, isCountyOrCity, compareMode, stateAbbr]);

  useEffect(() => {
    if (!selectedChartOption) return;

    if (selectedChartOption === 'Crime Rate') {
      setSelectedChart('Crime Rate');
      return;
    }
    
    if (isCountyOrCity || isMetro) {
      if (isMetro && (selectedChartOption.includes('Rank') || selectedChartOption.includes('Percentile'))) {
        setSelectedChart(selectedChartOption);
        return;
      }

      const currentPrefix = getComparisonLabel();
      const hasCorrectPrefix = selectedChartOption.includes(currentPrefix);
      
      if (!hasCorrectPrefix) {
        if (selectedChartOption.includes('vs')) {
          setSelectedChart(`Crime Rate vs ${currentPrefix} Rate`);
        } 
        else if (selectedChartOption.includes('%') && selectedChartOption.includes('Rate Difference')) {
          setSelectedChart(`% ${currentPrefix} Rate Difference`);
        }
        else if (selectedChartOption.includes('Difference')) {
          setSelectedChart(`${currentPrefix} Rate Difference`);
        }
        else if (selectedChartOption.includes('Rank')) {
          setSelectedChart(`${currentPrefix} Rank`);
        }
        else if (selectedChartOption.includes('Percentile')) {
          setSelectedChart(`${currentPrefix} Percentile`);
        }
        else {
          setSelectedChart(`${currentPrefix} ${selectedChartOption.split(' ').slice(-1)[0]}`);
        }
      } else {
        setSelectedChart(selectedChartOption);
      }
    } else {
      setSelectedChart(selectedChartOption);
    }
  }, [selectedChartOption, getComparisonLabel, isCountyOrCity, isMetro]);

  const comparisonLabelStr = getComparisonLabel();
  const chartOptions = useMemo(
    () => [
      'Crime Rate',
      `${(isCountyOrCity || isMetro) ? comparisonLabelStr : 'National'} Rate`,
      `${(isCountyOrCity || isMetro) ? comparisonLabelStr : 'National'} Rate Difference`,
      `% ${(isCountyOrCity || isMetro) ? comparisonLabelStr : 'National'} Rate Difference`,
      `Crime Rate vs ${(isCountyOrCity || isMetro) ? comparisonLabelStr : 'National'} Rate`,
      `${isCountyOrCity ? comparisonLabelStr : locationType} Rank`,
      `${isCountyOrCity ? comparisonLabelStr : locationType} Percentile`,
    ],
    [comparisonLabelStr, isCountyOrCity, isMetro, locationType]
  );

  const chartToUse = useMemo(
    () =>
      resolveRatesChartMenuSelection({
        selectedChart: selectedChartState,
        selectedChartOption,
        chartOptions,
        comparisonLabel: comparisonLabelStr,
        locationType,
        isCountyOrCity,
        isMetro,
      }),
    [
      selectedChartState,
      selectedChartOption,
      chartOptions,
      comparisonLabelStr,
      locationType,
      isCountyOrCity,
      isMetro,
    ]
  );

  useLayoutEffect(() => {
    if (!chartToUse || !onChartOptionChange) return;
    if (chartToUse !== selectedChartOption) {
      onChartOptionChange(chartToUse);
    }
  }, [chartToUse, selectedChartOption, onChartOptionChange]);

  const handleTouchStart = () => {
    setIsTouching(true);
  };

  const handleTouchEnd = () => {
    setIsTouching(false);
  };  

  const getChartOption = (baseOption) => {
    if (baseOption === 'Crime Rate') {
      return baseOption;
    }
    
    if (isCountyOrCity || isMetro) {
      if (isMetro && (baseOption.includes('Rank') || baseOption.includes('Percentile'))) {
        return baseOption;
      }

      const comparisonLabel = getComparisonLabel();
      
      if (baseOption.includes('Rank') || baseOption.includes('Percentile')) {
        return comparisonLabel + baseOption.substring(baseOption.indexOf(' '));
      }
      
      return baseOption.replace(/National/, comparisonLabel);
    }
    
    return baseOption;
  };

  const getTotalLocations = () => {
    if (!isCountyOrCity) {
      return data.current.total_locations || 0;
    }
    
    if (!data?.historical) {
      return compareMode.rateScope === 'state' ? 
        (isCounty ? data.current.total_counties_state : data.current.total_cities_state) || 0 : 
        (isCounty ? data.current.total_counties_national : data.current.total_cities_national) || 0;
    }
    
    let maxLocations = 0;
    
    data.historical.forEach(yearData => {
      const totalLocations = compareMode.rateScope === 'state' ? 
        (isCounty ? yearData.total_counties_state : yearData.total_cities_state) : 
        (isCounty ? yearData.total_counties_national : yearData.total_cities_national);
      
      if (totalLocations && totalLocations > maxLocations) {
        maxLocations = totalLocations;
      }
    });
    
    if (maxLocations === 0 && data.current) {
      return compareMode.rateScope === 'state' ? 
        (isCounty ? data.current.total_counties_state : data.current.total_cities_state) || 0 : 
        (isCounty ? data.current.total_counties_national : data.current.total_cities_national) || 0;
    }
    
    return maxLocations;
  };

  const extractNumber = (rank) => {
    if (typeof rank !== 'string') return rank;
    const match = rank.match(/\d+/);
    return match ? parseInt(match[0], 10) : rank;
  };

  const roundNumber = (value) => {
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    const normalized = value / magnitude;
    const rounded = Math.ceil(normalized / 0.1) * 0.1;
    return rounded * magnitude;
  };

  const transformData = () => {
    if (!data?.historical) {
      return [];
    }
  
    const getComparisonFields = (yearData, crimeName) => {
      const prefix = crimeName.toLowerCase();
      let avgRateField, rankField, rateDiffField, rateDiffPctField, percentileField;
  
      if (isMetro) {
        rankField = `${prefix}_national_rate_rank`;
        percentileField = `${prefix}_national_rate_percentile`;
        if (compareMode.rateType === 'metro') {
          avgRateField = `${prefix}_all_metros_rate`;
          rateDiffField = `${prefix}_all_metros_rate_diff`;
          rateDiffPctField = `${prefix}_all_metros_rate_diff_pct`;
        } else {
          avgRateField = `${prefix}_national_rate`;
          rateDiffField = `${prefix}_national_rate_diff`;
          rateDiffPctField = `${prefix}_national_rate_diff_pct`;
        }
      } else if (isCountyOrCity) {
        if (compareMode.rateType === 'county' || (compareMode.rateType === 'city' && isCity)) {
          if (compareMode.rateScope === 'state') {
            const locTypePrefix = isCounty ? 'counties' : 'cities';
            rankField = `${prefix}_state_rate_rank`;
            percentileField = `${prefix}_state_rate_percentile`;
            avgRateField = `${prefix}_all_${locTypePrefix}_state_rate`;
            rateDiffField = `${prefix}_all_${locTypePrefix}_state_rate_diff`;
            rateDiffPctField = `${prefix}_all_${locTypePrefix}_state_rate_diff_pct`;
          } else { 
            const locTypePrefix = isCounty ? 'counties' : 'cities';
            rankField = `${prefix}_national_rate_rank`;
            percentileField = `${prefix}_national_rate_percentile`;
            avgRateField = `${prefix}_all_${locTypePrefix}_national_rate`;
            rateDiffField = `${prefix}_all_${locTypePrefix}_national_rate_diff`;
            rateDiffPctField = `${prefix}_all_${locTypePrefix}_national_rate_diff_pct`;
          }
        } else { 
          if (compareMode.rateScope === 'state') {
            rankField = `${prefix}_state_rate_rank`;
            percentileField = `${prefix}_state_rate_percentile`;
            avgRateField = `${prefix}_state_rate`;
            rateDiffField = `${prefix}_state_rate_diff`;
            rateDiffPctField = `${prefix}_state_rate_diff_pct`;
          } else { 
            rankField = `${prefix}_national_rate_rank`;
            percentileField = `${prefix}_national_rate_percentile`;
            avgRateField = `${prefix}_national_rate`;
            rateDiffField = `${prefix}_national_rate_diff`;
            rateDiffPctField = `${prefix}_national_rate_diff_pct`;
          }
        }
      } else {
        rankField = `${prefix}_rate_rank`;
        percentileField = `${prefix}_rate_percentile`;
        avgRateField = `${prefix}_national_rate`;
        rateDiffField = `${prefix}_national_rate_diff`;
        rateDiffPctField = `${prefix}_national_rate_diff_pct`;
      }
      
      return {
        nationalRate: yearData[avgRateField],
        difference: yearData[rateDiffField],
        percentDiff: yearData[rateDiffPctField],
        rank: yearData[rankField],
        rankValue: extractNumber(yearData[rankField]),
        percentile: yearData[percentileField]
      };
    };
  
    const rawData = data.historical
      .sort((a, b) => a.year - b.year)
      .map(yearData => {
        const crimeField = `${selectedCrime.toLowerCase()}_rate`;
        const rate = yearData[crimeField];
  
        if (rate === null || rate === undefined) return null;
  
        return {
          year: yearData.year,
          rate,
          ...getComparisonFields(yearData, selectedCrime)
        };
      })
      .filter(row => row !== null);

    if (rawData.length === 0) return rawData;

    const availableYears = rawData.map(item => item.year);
    const minYear = Math.min(...availableYears);
    const maxYear = Math.max(...availableYears);
    
    const dataMap = new Map();
    rawData.forEach(item => {
      dataMap.set(item.year, item);
    });
    
    const completeData = [];
    for (let year = minYear; year <= maxYear; year++) {
      if (dataMap.has(year)) {
        completeData.push(dataMap.get(year));
      } else {
        completeData.push({
          year,
          rate: null,
          nationalRate: null,
          difference: null,
          percentDiff: null,
          rank: null,
          rankValue: null,
          percentile: null
        });
      }
    }

    return completeData;
  };

  const chartData = transformData();

  if (!chartData.length || (selectedSource === 'BJS_NCVS' && selectedCrime === 'all_crime')) {
    return (
      <div className="loading">
        {selectedSource === 'BJS_NCVS' && selectedCrime === 'all_crime' 
          ? <div style={{ marginTop: '-80px' }}>Historical Rates are not available for All Crime for<br />the National Crime Victimization Survey</div>
          : 'No Historical Data Available'
        }
      </div>
    );
  }

  const getMaxValue = () => {
    switch (chartToUse) {
      case getChartOption(`Crime Rate`):
      case getChartOption(`National Rate`):
        const maxValue = chartToUse === getChartOption(`Crime Rate`) 
          ? Math.max(...chartData.map(d => d.rate || 0))
          : Math.max(...chartData.map(d => d.nationalRate || 0));
        return roundNumber(maxValue);
      case getChartOption(`National Rate Difference`):
      case getChartOption(`% National Rate Difference`):
        const values = chartData.map(d => 
          chartToUse === getChartOption(`National Rate Difference`) 
            ? d.difference 
            : d.percentDiff
        );
        const maxAbs = Math.max(Math.abs(Math.min(...values)), Math.max(...values));
        return roundNumber(maxAbs);
      case getChartOption(`${locationType} Rank`):
        return getTotalLocations();
      case getChartOption(`${locationType} Percentile`):
        return 100;
      default:
        return roundNumber(Math.max(
          ...chartData.map(d => Math.max(d.rate || 0, d.nationalRate || 0))
        ));
    }
  };

  const getTicks = (maxVal) => {
    if (chartToUse === getChartOption(`${locationType} Rank`)) {
      return [getTotalLocations(), 
              Math.ceil(getTotalLocations()*3/4), 
              Math.ceil(getTotalLocations()/2), 
              Math.ceil(getTotalLocations()/4), 
              1];
    } else if (chartToUse === getChartOption(`${locationType} Percentile`)) {
      return [0, 25, 50, 75, 100];
    } else if (chartToUse.includes('Difference')) {
      const values = chartData.map(d => 
        chartToUse === getChartOption(`National Rate Difference`) 
          ? d.difference 
          : d.percentDiff
      );
      const minVal = Math.min(...values);
      const maxVal = Math.max(...values);
      const roundedMax = roundNumber(Math.max(Math.abs(minVal), maxVal));
      
      if (minVal < 0 && maxVal > 0) {
        return [-roundedMax, -roundedMax/2, 0, roundedMax/2, roundedMax];
      } else if (minVal < 0) {
        return [-roundedMax, -roundedMax*3/4, -roundedMax/2, -roundedMax/4, 0];
      } else {
        return [0, roundedMax/4, roundedMax/2, roundedMax*3/4, roundedMax];
      }
    } else {
      const maxValue = roundNumber(maxVal);
      return [0, maxValue/4, maxValue/2, maxValue*3/4, maxValue];
    }
  };

  const getCustomTicks = () => {
    if (!chartData || chartData.length === 0) return [];
    return chartData
      .filter(item => item.rate !== null)
      .map(item => item.year)
      .sort((a, b) => a - b);
  };

  const getYAxisDomain = () => {
    switch (chartToUse) {
      case getChartOption(`${locationType} Rank`):
        const buffer = getTotalLocations() * 0.05;
        return [1 - buffer, getTotalLocations() + buffer];
      case getChartOption(`${locationType} Percentile`):
        return [-5, 105];
      case getChartOption(`National Rate Difference`):
      case getChartOption(`% National Rate Difference`):
        const values = chartData.map(d => 
          chartToUse === getChartOption(`National Rate Difference`) 
            ? d.difference 
            : d.percentDiff
        );
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        const roundedMax = roundNumber(Math.max(Math.abs(minVal), maxVal));
        
        if (minVal < 0 && maxVal > 0) {
          return [-roundedMax, roundedMax];
        } else if (minVal < 0) {
          return [-roundedMax, 0];
        } else {
          return [0, roundedMax];
        }
      default:
        return [0, getMaxValue()];
    }
  };

  const renderCustomLegend = () => {
    const renderLegendIcon = () => {
      const lineStrokeWidth = 2;
      const dotRadius = 4; 

      switch (chartToUse) {
        case getChartOption(`National Rate Difference`):
        case getChartOption(`% National Rate Difference`):
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <svg width={isMobile ? '60' : '80'} height="12">
                <line
                  x1="0"
                  y1="6"
                  x2={isMobile ? '30' : '40'}
                  y2="6"
                  stroke="rgba(0, 128, 0, 0.3)"
                  strokeWidth={6}
                />
                <line
                  x1="0"
                  y1="6"
                  x2={isMobile ? '60' : '80'}
                  y2="6"
                  stroke="rgba(0, 0, 0, 0.3)"
                  strokeWidth={lineStrokeWidth}
                />
                <line
                  x1={isMobile ? '30' : '40'}
                  y1="6"
                  x2={isMobile ? '60' : '80'}
                  y2="6"
                  stroke="rgba(255, 0, 0, 0.3)"
                  strokeWidth={6}
                />
                <circle
                  cx={isMobile ? '30' : '40'}
                  cy="6"
                  r={dotRadius}
                  fill="rgba(0, 0, 0, 0.3)"
                  stroke="rgba(0, 0, 0, 0.3)"
                  strokeWidth={2}
                />
              </svg>
            </div>
          );

        case getChartOption(`${locationType} Rank`):
        case getChartOption(`${locationType} Percentile`):
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <svg width="12" height="12">
                <circle
                  cx="6"
                  cy="6"
                  r={dotRadius}
                  fill="rgba(26, 44, 78, 0.6)"
                  stroke="rgba(26, 44, 78, 0.6)"
                  strokeWidth={2}
                />
              </svg>
            </div>
          );

        default:
          return (
            <svg width={isMobile ? '60' : '80'} height="12">
              <line
                x1="0"
                y1="6"
                x2={isMobile ? '60' : '80'}
                y2="6"
                stroke="rgba(26, 44, 78, 0.6)"
                strokeWidth={lineStrokeWidth}
                strokeDasharray={chartToUse === getChartOption(`National Rate`) ? '6 6' : 'none'}
              />
              <circle
                cx={isMobile ? '30' : '40'}
                cy="6"
                r={dotRadius}
                fill="rgba(26, 44, 78, 0.6)"
                stroke="rgba(26, 44, 78, 0.6)"
                strokeWidth={2}
              />
            </svg>
          );
      }
    };

    const getDisplayTitle = () => {
      if (chartToUse === getChartOption(`Crime Rate`)) {
        return `${crimeName} Rate`;
      } else if (chartToUse === getChartOption(`Crime Rate vs National Rate`)) {
        return `${crimeName} Rate`;
      }
      return chartToUse;
    };

    return (
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 0 10px',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {renderLegendIcon()}
          <div 
            ref={menuRef}
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: isNation ? 'default' : 'pointer',
              gap: '4px',
              position: 'relative'
            }}
            onClick={() => !isNation && setIsMenuOpen(!isMenuOpen)}
          >
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>{getDisplayTitle()}</span>
            {!isNation && (
              <>
                <ChevronDown className="chart-legend-chevron" style={{ width: '16px', height: '16px' }} />
              
                {isMenuOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: getDropdownLeftPosition(chartToUse),
                    backgroundColor: 'white',
                    border: '1px solid var(--silver)',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    minWidth: 'max-content',
                    marginTop: '4px',
                    whiteSpace: 'nowrap'
                  }}>
                    {chartOptions.map((option, index) => {
                      const displayText = option.includes('Crime Rate') ? 
                        option.replace('Crime', crimeName) : option;

                      return (
                        <div
                          key={option}
                          style={{
                            padding: '8px 12px',
                            cursor: chartToUse === option ? 'not-allowed' : 'pointer',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: 600,
                            textAlign: 'left',
                            backgroundColor: chartToUse === option ? 'var(--light-gray)' : 'transparent',
                            borderBottom: index < chartOptions.length - 1 ? '1px solid var(--border-color)' : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative',
                            boxSizing: 'border-box',
                            height: '31px'
                          }}
                          onMouseEnter={(e) => {
                            if (chartToUse !== option) {
                              e.currentTarget.style.backgroundColor = 'var(--silver)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (chartToUse !== option) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                          onClick={(e) => {
                            if (chartToUse === option) {
                              e.stopPropagation();
                              return;
                            }
                            e.stopPropagation();
                            setSelectedChart(option);
                            onChartOptionChange(option);
                            setIsMenuOpen(false);
                          }}
                        >
                          <span style={{ opacity: chartToUse === option ? 0.5 : 1 }}>
                            {displayText}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {chartToUse === getChartOption(`Crime Rate vs National Rate`) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width={isMobile ? '60' : '80'} height="12">
              <line
                x1="0"
                y1="6"
                x2={isMobile ? '60' : '80'}
                y2="6"
                stroke="rgba(26, 44, 78, 0.6)"
                strokeWidth={2}
                strokeDasharray="6 6"
              />
              <circle
                cx={isMobile ? '30' : '40'}
                cy="6"
                r={4}
                fill="rgba(26, 44, 78, 0.6)"
                stroke="rgba(26, 44, 78, 0.6)"
                strokeWidth={2}
              />
            </svg>
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>
              {isCountyOrCity || isMetro ? getComparisonLabel() : 'National'} Rate
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderCommonChartComponents = () => (
    <>
      <CartesianGrid stroke="var(--border-color)" strokeDasharray="none" />
      <XAxis 
        dataKey="year"
        axisLine={{ stroke: 'var(--border-color)' }}
        tickLine={{ stroke: 'var(--border-color)' }}
        tick={{ fontSize: isMobile ? 12 : 14, fontWeight: 400, fill: 'black' }}
        ticks={getCustomTicks()}
        domain={['dataMin', 'dataMax']}
        type="number"
        scale="linear"
        interval={0}
        allowDecimals={false}
        height={isMobile ? 20 : 30}
        tickFormatter={(value, index) => {
          const availableYears = getCustomTicks();
          const currentIndex = availableYears.indexOf(value);
          
          if (currentIndex === -1) return '';
          
          const totalYears = availableYears.length;
          
          let maxLabels;
          if (windowWidth <= 480) {
            maxLabels = 5; 
          } else if (windowWidth <= 767) {
            maxLabels = 7; 
          } else if (windowWidth <= 1023) {
            maxLabels = 9; 
          } else if (windowWidth <= 1300) {
            maxLabels = 7; 
          } else {
            maxLabels = 11; 
          }
          
          let minYearGap = 1;
          if (totalYears > 5) {
            if (windowWidth <= 480) {
              minYearGap = 3; 
            } else {
              minYearGap = 2; 
            }
          }
          
          if (currentIndex === 0 || currentIndex === totalYears - 1) {
            return value;
          }
          
          if (maxLabels <= 2) {
            return '';
          }
          
          const selectedIndices = [0, totalYears - 1];
          
          const additionalLabels = maxLabels - 2;
          for (let i = 1; i <= additionalLabels; i++) {
            const index = Math.round(((totalYears - 1) * i) / (additionalLabels + 1));
            
            const yearValue = availableYears[index];
            let meetsMinGap = true;
            
            for (const selectedIndex of selectedIndices) {
              const selectedYear = availableYears[selectedIndex];
              if (Math.abs(yearValue - selectedYear) < minYearGap) {
                meetsMinGap = false;
                break;
              }
            }
            
            if (meetsMinGap) {
              selectedIndices.push(index);
            }
          }
          
          return selectedIndices.includes(currentIndex) ? value : '';
        }}
      />
      <YAxis
        domain={getYAxisDomain()}
        ticks={getTicks(getMaxValue())}
        axisLine={{ stroke: 'var(--border-color)' }}
        tickLine={{ stroke: 'var(--border-color)' }}
        tickFormatter={(value) => {
          if (value === 0) return '0';
          if (chartToUse === getChartOption(`${locationType} Percentile`)) {
            return formatPercentile(value);
          } else if (chartToUse === getChartOption(`${locationType} Rank`)) {
            return formatRank(value);
          } else if (chartToUse === getChartOption(`National Rate Difference`)) {
            return value === 0 ? '0' : formatDifference(value, false, true);
          } else if (chartToUse === getChartOption(`% National Rate Difference`)) {
            return value === 0 ? '0' : formatDifference(value, true);
          } else {
            return formatNumber(value);
          }
        }}
        tick={props => {
          const { x, y, payload } = props;
          const domain = getYAxisDomain();
          const isBottomTick = payload.value === domain[0];
          const adjustedY = isBottomTick ? y - 5 : y;
          
          return (
            <text
              x={x}
              y={adjustedY}
              textAnchor="end"
              fill="black"
              fontSize={isMobile ? 12 : 14}
              fontWeight={400}
              dy={4}
            >
              {props.tickFormatter(payload.value)}
            </text>
          );
        }}
      />
      {chartToUse === getChartOption(`National Rate Difference`) && (
        <ReferenceLine
          y={1}
          stroke="var(--silver)"
          strokeDasharray="6 6"
          strokeWidth={2}
        />
      )}
      <Tooltip
        active={isMobile ? isTouching : undefined}
        content={({ active, payload, label }) => {
          if (active && payload && payload.length) {
            const data = payload[0].payload;
            const rateColor = getRateColor(data.rate, data.nationalRate);
            const rateDiffColor = getRateDiffColor(data.difference);
            const ratePercentDiffColor = getValueColor(data.percentDiff);
            
            const getRowStyle = (type) => ({
              display: 'flex', 
              justifyContent: 'space-between', 
              gap: '24px',
              backgroundColor: (
                (!isNation && chartToUse === getChartOption(`Crime Rate`) && type === 'Rate') ||
                (chartToUse === getChartOption(`National Rate`) && type === 'National Rate') ||
                (chartToUse === getChartOption(`National Rate Difference`) && type === 'National Rate Difference') ||
                (chartToUse === getChartOption(`% National Rate Difference`) && type === '% National Rate Difference') ||
                (chartToUse === getChartOption(`${locationType} Rank`) && type === 'Rank') ||
                (chartToUse === getChartOption(`${locationType} Percentile`) && type === 'Percentile') ||
                (chartToUse === getChartOption(`Crime Rate vs National Rate`) && 
                  (type === 'Rate' || type === 'National Rate' || type === 'National Rate Difference'))
              ) ? '#fef9c3' : 'transparent',
              padding: '2px 4px',
              margin: '0 -4px'
            });
            
            if (data.rate !== null) {
              return (
                <div className="custom-tooltip" style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '12px',
                  border: '1px solid var(--silver)',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 600,
                    fontSize: isMobile ? '13px' : '14px',
                    color: 'black',
                    textAlign: 'left',
                    paddingBottom: '2px',
                  }}>
                    {isCountyOrCity ? ((selectedLocation.info.name.split(',')[0]) + ', ' + stateAbbr) : selectedLocation.info.name}
                  </div>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 400,
                    fontSize: isMobile ? '13px' : '14px',
                    textAlign: 'left',
                    borderBottom: '1px solid var(--silver)',
                    paddingBottom: '8px'
                  }}>
                    {data.year}
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '12px', display: 'grid', gap: '4px' }}>
                    <div style={getRowStyle('Rate')}>
                      <span style={{ fontWeight: 600 }}>{crimeName} Rate:</span>
                      <span style={{ color: `var(--${rateColor})` }}>{formatNumber(data.rate)}</span>
                    </div>
                    {!isNation && (
                      <>
                        <div style={getRowStyle('National Rate')}>
                          <span style={{ fontWeight: 600 }}>
                            {isCountyOrCity || isMetro ? getComparisonLabel() : 'National'} Rate:
                          </span>
                          <span>{formatNumber(data.nationalRate)}</span>
                        </div>
                        <div style={getRowStyle('National Rate Difference')}>
                          <span style={{ fontWeight: 600 }}>
                            {isCountyOrCity || isMetro ? getComparisonLabel() : 'National'} Rate Difference:
                          </span>
                          <span style={{ color: `var(--${rateDiffColor})`, fontWeight: 600 }}>
                            {formatDifference(data.difference, false, true)}
                          </span>
                        </div>
                        <div style={getRowStyle('% National Rate Difference')}>
                          <span style={{ fontWeight: 600 }}>
                            % {isCountyOrCity || isMetro ? getComparisonLabel() : 'National'} Rate Difference:
                          </span>
                          <span style={{ color: `var(--${ratePercentDiffColor})` }}>
                            {formatDifference(data.percentDiff, true)}
                          </span>
                        </div>
                        <div style={getRowStyle('Rank')}>
                          <span style={{ fontWeight: 600 }}>
                            {isCountyOrCity ? getComparisonLabel() : locationType} Rank:
                          </span>
                          <span>{formatRank(data.rank)}</span>
                        </div>
                        <div style={getRowStyle('Percentile')}>
                          <span style={{ fontWeight: 600 }}>
                            {isCountyOrCity ? getComparisonLabel() : locationType} Percentile:
                          </span>
                          <span>{formatPercentile(data.percentile)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            } else {
              return (
                <div className="custom-tooltip" style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '12px',
                  border: '1px solid var(--silver)',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 600,
                    fontSize: isMobile ? '13px' : '14px',
                    color: 'black',
                    textAlign: 'left',
                    paddingBottom: '2px',
                  }}>
                    {isCountyOrCity ? ((selectedLocation.info.name.split(',')[0]) + ', ' + stateAbbr) : selectedLocation.info.name}
                  </div>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 400,
                    fontSize: isMobile ? '13px' : '14px',
                    textAlign: 'left',
                    borderBottom: '1px solid var(--silver)',
                    paddingBottom: '8px'
                  }}>
                    {data.year}
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '12px', padding: '20px' }}>
                    No data available
                  </div>
                </div>
              );
            }
          }
          return null;
        }}
      />
    </>
  );

  const getValue = (data) => {
    switch (chartToUse) {
      case getChartOption(`Crime Rate`):
        return data.rate;
      case getChartOption(`Crime Rate vs National Rate`):
        return {
          rate: data.rate,
          nationalRate: data.nationalRate
        };
      case getChartOption(`National Rate`):
        return data.nationalRate;
      case getChartOption(`National Rate Difference`):
        return data.difference;
      case getChartOption(`% National Rate Difference`):
        return data.percentDiff;
      case getChartOption(`${locationType} Rank`):
        return data.rankValue;
      case getChartOption(`${locationType} Percentile`):
        return data.percentile;
      default:
        return 0;
    }
  };
  
  return (
    <div className={`chart-container ${isMenuOpen ? 'active' : ''}`}>
      {renderCustomLegend()}
      <div 
        className="chart-content"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <ResponsiveContainer>
          {chartToUse === getChartOption(`Crime Rate vs National Rate`) ? (
            <AreaChart
              key="rate-vs-national"
              data={chartData}
              margin={{ top: 0, right: isMobile ? 30 : 40, left: isMobile ? 20 : 30, bottom: 0 }}
            >
              {renderCommonChartComponents()}
              {(() => {
                const availableYears = chartData.filter(item => item.rate !== null).sort((a, b) => a.year - b.year);
                const gapSegments = [];
                
                for (let i = 0; i < availableYears.length - 1; i++) {
                  const currentPoint = availableYears[i];
                  const nextPoint = availableYears[i + 1];
                  
                  if (nextPoint.year - currentPoint.year > 1) {
                    gapSegments.push(
                      <React.Fragment key={`gap-fragment-${currentPoint.year}-${nextPoint.year}`}>
                        <Area
                          key={`gap-rate-${currentPoint.year}-${nextPoint.year}`}
                          type="monotone"
                          dataKey={(data) => {
                            if (data.year === currentPoint.year || data.year === nextPoint.year) {
                              return data.rate;
                            }
                            return null;
                          }}
                          stroke="var(--border-color)"
                          strokeWidth={2}
                          strokeDasharray="none"
                          fill="var(--border-color)"
                          dot={false}
                          activeDot={false}
                          connectNulls={true}
                        />
                        <Area
                          key={`gap-national-${currentPoint.year}-${nextPoint.year}`}
                          type="monotone"
                          dataKey={(data) => {
                            if (data.year === currentPoint.year || data.year === nextPoint.year) {
                              return data.nationalRate;
                            }
                            return null;
                          }}
                          stroke="var(--border-color)"
                          strokeWidth={2}
                          strokeDasharray="6 6"
                          fill="var(--border-color)"
                          dot={false}
                          activeDot={false}
                          connectNulls={true}
                        />  
                      </React.Fragment>
                    );
                  }
                }
                
                return gapSegments;
              })()}
              <Area
                dataKey={(data) => data.rate === null ? 0 : getValue(data)}
                stroke="transparent"
                strokeWidth={0}
                dot={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="rate"
                stroke="none" 
                fill="rgba(255, 0, 0, 0.3)"
                dot={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="nationalRate"
                stroke="none"
                fill="rgba(0, 128, 0, 0.3)"
                dot={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="rate"
                stroke="rgba(26, 44, 78, 0.6)"
                fill="none"
                strokeWidth={2}
                dot={false}
                activeDot={isMobile ? (isTouching ? {
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)'
                } : false) : { 
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)' 
                }}
              />
              <Area
                type="monotone"
                dataKey="nationalRate"
                stroke="rgba(26, 44, 78, 0.6)"
                fill="none"
                strokeWidth={2}
                strokeDasharray="6 6"
                dot={false}
                activeDot={isMobile ? (isTouching ? {
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)'
                } : false) : { 
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)' 
                }}
              />
            </AreaChart>
          ) : (chartToUse === getChartOption(`National Rate Difference`)) ? ( 
            <AreaChart
              data={chartData}
              margin={{ top: 0, right: isMobile ? 30 : 40, left: isMobile ? 20 : 30, bottom: 0 }}
            >
              {renderCommonChartComponents()}
              {(() => {
                const availableYears = chartData.filter(item => item.rate !== null).sort((a, b) => a.year - b.year);
                const gapSegments = [];
                
                for (let i = 0; i < availableYears.length - 1; i++) {
                  const currentPoint = availableYears[i];
                  const nextPoint = availableYears[i + 1];
                  
                  if (nextPoint.year - currentPoint.year > 1) {
                    gapSegments.push(
                      <Area
                        key={`gap-${currentPoint.year}-${nextPoint.year}`}
                        type="monotone"
                        dataKey={(data) => {
                          if (data.year === currentPoint.year || data.year === nextPoint.year) {
                            return getValue(data);
                          }
                          return null;
                        }}
                        stroke="var(--border-color)"
                        strokeWidth={2}
                        strokeDasharray="none"
                        fill="var(--border-color)"
                        dot={false}
                        activeDot={false}
                        connectNulls={true}
                      />
                    );
                  }
                }
                
                return gapSegments;
              })()}
              <Area
                type="monotone"
                dataKey={(data) => {
                  const value = getValue(data);
                  return value > 1 ? value : 1;
                }}
                stroke="none"
                fill="rgba(255, 0, 0, 0.3)"
                strokeWidth={2}
                dot={false}
                activeDot={false}
                baseValue={1}
              />
              <Area
                type="monotone"
                dataKey={(data) => {
                  const value = getValue(data);
                  return value < 1 ? value : 1;
                }}
                stroke="none"
                fill="rgba(0, 128, 0, 0.3)"
                strokeWidth={2}
                dot={false}
                activeDot={false}
                baseValue={1}
              />
              <Area
                type="monotone"
                dataKey={(data) => getValue(data)}
                stroke="rgba(0, 0, 0, 0.3)"
                fill="none"
                strokeWidth={2}
                dot={false}
                activeDot={isMobile ? (isTouching ? {
                  r: 4, 
                  stroke: 'rgba(0, 0, 0, 0.3)'
                } : false) : { 
                  r: 4, 
                  stroke: 'rgba(0, 0, 0, 0.3)' 
                }}
              />
            </AreaChart>
          ) : (chartToUse === getChartOption(`% National Rate Difference`)) ? ( 
            <AreaChart
              data={chartData}
              margin={{ top: 0, right: isMobile ? 30 : 40, left: isMobile ? 20 : 30, bottom: 0 }}
            >
              {renderCommonChartComponents()}
              {(() => {
                const availableYears = chartData.filter(item => item.rate !== null).sort((a, b) => a.year - b.year);
                const gapSegments = [];
                
                for (let i = 0; i < availableYears.length - 1; i++) {
                  const currentPoint = availableYears[i];
                  const nextPoint = availableYears[i + 1];
                  
                  if (nextPoint.year - currentPoint.year > 1) {
                    gapSegments.push(
                      <Area
                        key={`gap-${currentPoint.year}-${nextPoint.year}`}
                        type="monotone"
                        dataKey={(data) => {
                          if (data.year === currentPoint.year || data.year === nextPoint.year) {
                            return getValue(data);
                          }
                          return null;
                        }}
                        stroke="var(--border-color)"
                        strokeWidth={2}
                        strokeDasharray="none"
                        fill="var(--border-color)"
                        dot={false}
                        activeDot={false}
                        connectNulls={true}
                      />
                    );
                  }
                }
                
                return gapSegments;
              })()}
              <Area
                type="monotone"
                dataKey={(data) => {
                  const value = getValue(data);
                  return value > 0 ? value : 0;
                }}
                stroke="none"
                fill="rgba(255, 0, 0, 0.3)"
                strokeWidth={2}
                dot={false}
                activeDot={false}
                baseValue={0}
              />
              <Area
                type="monotone"
                dataKey={(data) => {
                  const value = getValue(data);
                  return value < 0 ? value : 0;
                }}
                stroke="none"
                fill="rgba(0, 128, 0, 0.3)"
                strokeWidth={2}
                dot={false}
                activeDot={false}
                baseValue={0}
              />
              <Area
                type="monotone"
                dataKey={(data) => getValue(data)}
                stroke="rgba(0, 0, 0, 0.3)"
                fill="none"
                strokeWidth={2}
                dot={false}
                activeDot={isMobile ? (isTouching ? {
                  r: 4, 
                  stroke: 'rgba(0, 0, 0, 0.3)'
                } : false) : { 
                  r: 4, 
                  stroke: 'rgba(0, 0, 0, 0.3)' 
                }}
              />
            </AreaChart>
          ) : (
            <LineChart
              data={chartData}
              margin={{ top: 0, right: isMobile ? 30 : 40, left: isMobile ? (chartToUse.includes('Rank') || chartToUse.includes('Percentile') ? -10 : 20) : 30, bottom: 0 }}
            >
              {renderCommonChartComponents()}
              {(() => {
                const availableYears = chartData.filter(item => item.rate !== null).sort((a, b) => a.year - b.year);
                const gapSegments = [];
                
                for (let i = 0; i < availableYears.length - 1; i++) {
                  const currentPoint = availableYears[i];
                  const nextPoint = availableYears[i + 1];
                  
                  if (nextPoint.year - currentPoint.year > 1) {
                    gapSegments.push(
                      <Line
                        key={`gap-${currentPoint.year}-${nextPoint.year}`}
                        type="monotone"
                        dataKey={(data) => {
                          if (data.year === currentPoint.year || data.year === nextPoint.year) {
                            return getValue(data);
                          }
                          return null;
                        }}
                        stroke={chartToUse.includes('Rank') || chartToUse.includes('Percentile') ? 'transparent' : 'var(--border-color)'}
                        strokeWidth={2}
                        strokeDasharray={chartToUse === getChartOption(`National Rate`) ? '6 6' : 'none'}
                        dot={false}
                        activeDot={false}
                        connectNulls={true}
                      />
                    );
                  }
                }
                
                return gapSegments;
              })()}
              <Line 
                dataKey={(data) => data.rate === null ? 0 : getValue(data)}
                stroke="transparent"
                strokeWidth={0}
                dot={false}
                activeDot={false}
              />
              <Line
                type="monotone"
                dataKey={(data) => getValue(data)}
                stroke={chartToUse.includes('Rank') || chartToUse.includes('Percentile') ? 'none' : 'rgba(26, 44, 78, 0.6)'}
                strokeWidth={2}
                strokeDasharray={chartToUse === getChartOption(`National Rate`) ? '6 6' : 'none'}
                dot={chartToUse.includes('Rank') || chartToUse.includes('Percentile') ? {
                  r: 4,
                  fill: 'var(--navy)',
                  fillOpacity: 0.6,
                  stroke: 'rgba(26, 44, 78, 0.6)'
                } : false}
                activeDot={isMobile ? (isTouching ? (chartToUse.includes('Rank') || chartToUse.includes('Percentile') ? {
                  r: 4,
                  fill: 'var(--navy)',
                  fillOpacity: 0.9,
                  stroke: 'rgba(26, 44, 78, 0.9)'
                } : { 
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)' 
                }) : false) : (chartToUse.includes('Rank') || chartToUse.includes('Percentile') ? {
                  r: 4,
                  fill: 'var(--navy)',
                  fillOpacity: 0.6,
                  stroke: 'rgba(26, 44, 78, 0.6)'
                } : { 
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)' 
                })}
                animationDuration={chartToUse.includes('Rank') || chartToUse.includes('Percentile') ? 0 : 1000}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default HistoricalRatesChart;